import React, { useEffect, useState, useCallback } from 'react';
import {
  Workflow, Database, FileText, ExternalLink, Upload, Loader2,
  AlertTriangle, RefreshCw, ShieldCheck, Trash2, Lock,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../index';
import {
  getWorkflow, getSkillConnectors, listPolicyDocuments, getConnectors,
  listSkillPolicyBindings, unbindSkillPolicyControl,
} from '../../../services/api';

/**
 * P0-2 follow-up — Read-only "linked resource" panel that mounts
 * inside the AgentDetailPageV2 skill edit modal for the three
 * non-wasm skill kinds that bind to an external artifact.
 *
 * <p>Achieves UI parity with the WasmSkillUploader pattern without
 * needing the backend re-link endpoints that would be required for
 * a full editor. Surface shows:</p>
 *
 * <ul>
 *   <li><b>workflow_skill</b> — the linked workflow's name +
 *       "Open in Canvas" deep-link.</li>
 *   <li><b>connector_skill</b> — the list of currently-linked
 *       connector names.</li>
 *   <li><b>policy_skill</b> — count of attached policy documents +
 *       "Manage documents" deep-link to the existing upload modal
 *       on the parent page (triggered via onRequestPolicyUpload).</li>
 * </ul>
 *
 * <p>To CHANGE the binding the operator follows the deep-link, OR
 * (per the Sprint 1 design) deletes + re-creates the skill. A
 * follow-up sprint will add the missing POST endpoints and a real
 * editor; until then the panel is honest about the read-only
 * status rather than pretending to support mutation.</p>
 *
 * <h3>Props</h3>
 * <ul>
 *   <li><code>skill</code> — required PolicyRule-shaped skill row
 *       (must carry skillType + linkedResourceId where relevant).</li>
 *   <li><code>agentId</code> — required UUID, used for the
 *       getSkillConnectors call and Canvas deep-link.</li>
 *   <li><code>onRequestPolicyUpload</code> — optional callback,
 *       fired when the user clicks "Manage documents" on a
 *       policy_skill. The parent page already wires the document
 *       upload modal; the panel just triggers it.</li>
 * </ul>
 */
export function SkillLinkedResourcePanel({
  skill,
  agentId,
  onRequestPolicyUpload,
  reloadToken,   // bumped by the parent after a successful doc upload → auto-refresh
  /* RC1 — true when the owning agent is external (ExteLet). External policy
   * skills materialize their runtime enforcement through the governance-policy
   * journey on the Build tab (Save & Materialize), NOT through the /policy-config
   * wizard — that wizard only flips activation status and never materializes the
   * bridge bindings, so pointing an ExteLet there re-introduces empty bindings.
   * When external, the /policy-config "Configure / Bind" affordances are hidden.
   * NATIVE agents (isExternal=false) keep /policy-config as their path. */
  isExternal = false,
}) {
  const navigate = useNavigate();
  const kind = skill?.skillType;

  // Nothing to render for standard / wasm_skill rows — wasm has its
  // own uploader, standard has no linked resource.
  if (!skill || !kind || kind === 'standard' || kind === 'wasm_skill') {
    return null;
  }

  if (kind === 'workflow_skill') {
    return <WorkflowSkillPanel skill={skill} agentId={agentId} navigate={navigate} />;
  }
  if (kind === 'connector_skill') {
    return <ConnectorSkillPanel skill={skill} agentId={agentId} />;
  }
  if (kind === 'policy_skill') {
    return <PolicySkillPanel
      skill={skill}
      agentId={agentId}
      isExternal={isExternal}
      onRequestPolicyUpload={onRequestPolicyUpload}
      reloadToken={reloadToken}
    />;
  }
  return null;
}

// ─── workflow_skill ───────────────────────────────────────────

function WorkflowSkillPanel({ skill, agentId, navigate }) {
  const workflowId = skill.linkedResourceId
    || skill.params?.workflowId
    || skill.linkedWorkflowId
    || null;
  const [workflow, setWorkflow] = useState(null);
  const [state, setState] = useState('idle'); // idle | loading | error | ready
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!workflowId) {
      setState('ready');
      setWorkflow(null);
      return;
    }
    setState('loading');
    setError(null);
    const r = await getWorkflow(workflowId);
    if (r?.ok === false) {
      setState('error');
      setError(r.error || 'workflow lookup failed');
      return;
    }
    const wf = r?.data || r;
    setWorkflow(wf);
    setState('ready');
  }, [workflowId]);

  useEffect(() => { load(); }, [load]);

  const wfName = workflow?.name || workflow?.displayName || workflow?.id;
  const wfStatus = workflow?.status || workflow?.phase;

  return (
    <Wrapper
      icon={<Workflow size={14} className="text-v2-action-strong" />}
      title="Linked workflow"
      subtitle="The canvas workflow this skill executes when invoked."
    >
      {state === 'loading' && (
        <div className="text-[12px] text-v2-ink-muted flex items-center gap-2">
          <Loader2 size={13} className="animate-spin" /> Looking up workflow…
        </div>
      )}
      {state === 'error' && (
        <ErrorRow text={error} />
      )}
      {state === 'ready' && !workflowId && (
        <Info inline iconColor="warning">
          This skill has no <code className="font-mono-v2 text-[11px]">linkedResourceId</code>.
          That's unusual for a workflow_skill — verify it was created correctly.
        </Info>
      )}
      {state === 'ready' && workflowId && !workflow && (
        <Info inline iconColor="warning">
          Workflow <code className="font-mono-v2 text-[11px]">{workflowId}</code> not found.
          It may have been deleted; this skill will fail to execute.
        </Info>
      )}
      {state === 'ready' && workflow && (
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="text-[12.5px] font-semibold text-v2-ink truncate">
              {wfName}
            </div>
            <div className="text-[10.5px] text-v2-ink-muted mt-0.5">
              <code className="font-mono-v2">{workflowId}</code>
              {wfStatus && <span className="ml-2">· {wfStatus}</span>}
            </div>
          </div>
          <Button
            variant="secondary" size="sm"
            iconLeft={<ExternalLink size={12} />}
            onClick={() => navigate(`/canvas?agentId=${agentId}&workflowId=${workflowId}`)}
          >
            Open in Canvas
          </Button>
        </div>
      )}
      <ReadOnlyHint kind="workflow" />
    </Wrapper>
  );
}

// ─── connector_skill ──────────────────────────────────────────

function ConnectorSkillPanel({ skill, agentId }) {
  const [linkedConnectorIds, setLinkedConnectorIds] = useState([]);
  const [allConnectors, setAllConnectors] = useState([]);
  const [state, setState] = useState('loading');
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setState('loading');
    setError(null);
    const [linksRes, allRes] = await Promise.all([
      getSkillConnectors(agentId, skill.id).catch(() => null),
      getConnectors().catch(() => null),
    ]);
    if (linksRes?.ok === false) {
      setState('error');
      setError(linksRes.error || 'connector lookup failed');
      return;
    }
    const links = Array.isArray(linksRes?.data) ? linksRes.data
                : Array.isArray(linksRes?.data?.items) ? linksRes.data.items
                : Array.isArray(linksRes) ? linksRes : [];
    setLinkedConnectorIds(links.map((l) => l.connectorId || l.id).filter(Boolean));
    const all = Array.isArray(allRes?.data) ? allRes.data
              : Array.isArray(allRes) ? allRes : [];
    setAllConnectors(all);
    setState('ready');
  }, [agentId, skill.id]);

  useEffect(() => { load(); }, [load]);

  const connNameById = React.useMemo(() => {
    const map = Object.create(null);
    allConnectors.forEach((c) => { if (c?.id) map[String(c.id)] = c.name || c.displayName; });
    return map;
  }, [allConnectors]);

  return (
    <Wrapper
      icon={<Database size={14} className="text-v2-action-strong" />}
      title={`Linked connectors${state === 'ready' ? ` · ${linkedConnectorIds.length}` : ''}`}
      subtitle="Connectors this skill is permitted to invoke."
    >
      {state === 'loading' && (
        <div className="text-[12px] text-v2-ink-muted flex items-center gap-2">
          <Loader2 size={13} className="animate-spin" /> Loading connector list…
        </div>
      )}
      {state === 'error' && <ErrorRow text={error} />}
      {state === 'ready' && linkedConnectorIds.length === 0 && (
        <Info inline iconColor="warning">
          No connectors linked. This connector_skill will fail at execution time
          because the gate won't have a callable backend to dispatch to.
        </Info>
      )}
      {state === 'ready' && linkedConnectorIds.length > 0 && (
        <ul className="space-y-1.5">
          {linkedConnectorIds.map((cid) => (
            <li key={cid}
                className="flex items-baseline gap-2 text-[12px] text-v2-ink">
              <Database size={11} className="text-v2-ink-muted shrink-0" />
              <span className="font-semibold">
                {connNameById[String(cid)] || <em className="not-italic text-v2-ink-muted">unknown connector</em>}
              </span>
              <code className="ml-auto font-mono-v2 text-[10.5px] text-v2-ink-muted">
                {String(cid).slice(0, 12)}…
              </code>
            </li>
          ))}
        </ul>
      )}
      <ReadOnlyHint kind="connector" />
    </Wrapper>
  );
}

// ─── policy_skill ─────────────────────────────────────────────

function PolicySkillPanel({ skill, agentId, isExternal = false, onRequestPolicyUpload, reloadToken }) {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [state, setState] = useState('loading');
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setState('loading');
    setError(null);
    const r = await listPolicyDocuments(skill.id);
    if (r?.ok === false) {
      setState('error');
      setError(r.error || 'document lookup failed');
      return;
    }
    const list = Array.isArray(r?.data) ? r.data
               : Array.isArray(r?.data?.items) ? r.data.items
               : Array.isArray(r) ? r : [];
    setDocs(list);
    setState('ready');
  }, [skill.id]);

  // Auto-refresh on mount AND whenever the parent bumps reloadToken (i.e. right
  // after a successful upload — V1 parity; previously the list only refreshed on
  // a manual Reload click). DEF-LLC-POLICYDOC-REFRESH-001.
  useEffect(() => { load(); }, [load, reloadToken]);

  const totalChunks = docs.reduce((n, d) => n + (d.chunkCount || 0), 0);

  return (
    <Wrapper
      icon={<FileText size={14} className="text-v2-warning-strong" />}
      title={`Policy documents${state === 'ready' ? ` · ${docs.length}` : ''}`}
      subtitle="RAG corpus this policy skill consults at enforcement time."
    >
      {state === 'loading' && (
        <div className="text-[12px] text-v2-ink-muted flex items-center gap-2">
          <Loader2 size={13} className="animate-spin" /> Loading documents…
        </div>
      )}
      {state === 'error' && <ErrorRow text={error} />}
      {state === 'ready' && (
        <div>
          {docs.length === 0 ? (
            <Info inline iconColor="warning">
              No policy documents uploaded yet. Upload at least one PDF / doc so
              the RAG retriever has something to consult.
            </Info>
          ) : (
            <>
              <ul className="space-y-1.5">
                {docs.slice(0, 6).map((d) => (
                  <li key={d.id}
                      className="flex items-baseline gap-2 text-[12px] text-v2-ink">
                    <FileText size={11} className="text-v2-ink-muted shrink-0" />
                    <span className="font-semibold truncate">
                      {d.filename || d.name || 'untitled'}
                    </span>
                    {/* Surface chunk count + parse state per doc (V1 parity). */}
                    {typeof d.chunkCount === 'number' && (
                      <span className="text-[10.5px] text-v2-ink-muted">· {d.chunkCount} chunk{d.chunkCount === 1 ? '' : 's'}</span>
                    )}
                    {d.parseStatus && d.parseStatus !== 'parsed' && (
                      <span className="text-[10px] text-v2-warning-strong">· {d.parseStatus}</span>
                    )}
                    {d.enforcement && (
                      <span className="text-[10.5px] text-v2-ink-muted ml-auto">{d.enforcement}</span>
                    )}
                  </li>
                ))}
                {docs.length > 6 && (
                  <li className="text-[11px] text-v2-ink-muted">
                    + {docs.length - 6} more…
                  </li>
                )}
              </ul>
              <div className="text-[10.5px] text-v2-ink-muted mt-1.5">
                {docs.length} document{docs.length === 1 ? '' : 's'} · {totalChunks} chunk{totalChunks === 1 ? '' : 's'} embedded (advisory RAG)
              </div>
            </>
          )}

          {/* Bound runtime controls — what is ACTUALLY enforced at runtime for
            * this skill (distinct from advisory RAG docs above). Lets the
            * operator see + remove bindings; re-binding happens in Policies
            * after removal (design §2.4). POLICY-PHASE2. */}
          <BoundControlsSection
            skillId={skill.id}
            skillPhase={skill.skillPhase}
            reloadToken={reloadToken}
            /* RC1 — the /policy-config wizard only flips activation and never
             * materializes bridge bindings, so it must not be offered for an
             * external agent's policy skill (it re-introduces empty bindings).
             * Withhold the "Bind controls in Policies" link when external —
             * BoundControlsSection hides it when onGoToBind is absent. Native
             * agents keep it. */
            onGoToBind={isExternal
              ? undefined
              : () => navigate(`/policy-config?skillId=${skill.id}&stage=bind${agentId ? `&agentId=${agentId}` : ''}`)}
          />

          {/* Bridge CTA — uploaded docs are advisory RAG only. To turn them into
            * deterministic runtime controls the operator continues in Policies
            * (Extract → Review → Bind → Activate). Deep-links pre-selecting this
            * policy skill at the Extract stage. DEF-LLC-POLICYDOC-BRIDGE-001.
            *
            * RC1 — NATIVE agents only. For an external agent, /policy-config does
            * not materialize the bridge bindings, so this deep-link is withheld;
            * runtime enforcement is authored + materialized from the governance
            * policy on the Build tab instead. */}
          {docs.length > 0 && !isExternal && (
            <div className="mt-3 rounded-v2-sm border border-v2-trust-border bg-v2-trust-soft px-2.5 py-2">
              <div className="text-[11.5px] text-v2-trust-strong">
                Indexed as <strong>advisory RAG</strong> ({totalChunks} chunk{totalChunks === 1 ? '' : 's'}).
                To enforce these as deterministic runtime controls, extract &amp; bind them in Policies.
              </div>
              <Button
                variant="primary" size="sm"
                iconLeft={<ExternalLink size={12} />}
                className="mt-2"
                onClick={() => navigate(`/policy-config?skillId=${skill.id}&stage=source${agentId ? `&agentId=${agentId}` : ''}`)}
              >
                Extract &amp; bind in Policies
              </Button>
            </div>
          )}
          {docs.length > 0 && isExternal && (
            <div className="mt-3 rounded-v2-sm border border-v2-trust-border bg-v2-trust-soft px-2.5 py-2">
              <div className="text-[11.5px] text-v2-trust-strong">
                Indexed as <strong>advisory RAG</strong> ({totalChunks} chunk{totalChunks === 1 ? '' : 's'}).
                Runtime enforcement for this external agent is configured from its governance policy
                on the Build tab, where saving materializes the controls.
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 mt-3">
            {typeof onRequestPolicyUpload === 'function' && (
              <Button
                variant="secondary" size="sm"
                iconLeft={<Upload size={12} />}
                onClick={() => onRequestPolicyUpload(skill)}
              >
                {docs.length === 0 ? 'Upload first document' : 'Manage documents'}
              </Button>
            )}
            <Button
              variant="ghost" size="sm"
              iconLeft={<RefreshCw size={12} />}
              onClick={load}
            >
              Reload
            </Button>
          </div>
        </div>
      )}
    </Wrapper>
  );
}

// ─── shared chrome ────────────────────────────────────────────

function Wrapper({ icon, title, subtitle, children }) {
  return (
    <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md p-3">
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <h4 className="text-[12.5px] font-semibold text-v2-ink m-0">{title}</h4>
      </div>
      {subtitle && (
        <p className="text-[11px] text-v2-ink-secondary m-0 mb-2">{subtitle}</p>
      )}
      {children}
    </div>
  );
}

function ReadOnlyHint({ kind }) {
  return (
    <p className="mt-2 text-[10.5px] text-v2-ink-muted italic">
      Read-only — re-linking a {kind} after creation isn't yet wired.
      To change the binding, delete and recreate the skill. (Re-link endpoints
      ship in a follow-up sprint.)
    </p>
  );
}

function ErrorRow({ text }) {
  return (
    <div className="text-[11.5px] text-v2-warning-strong bg-v2-warning-soft border border-v2-warning/30 rounded-v2-sm px-2.5 py-1.5 flex items-center gap-2">
      <AlertTriangle size={12} /> {text}
    </div>
  );
}

function Info({ inline, iconColor = 'muted', children }) {
  const tone = iconColor === 'warning'
    ? 'bg-v2-warning-soft border-v2-warning/30 text-v2-warning-strong'
    : 'bg-v2-action-soft border-v2-action/30 text-v2-action-strong';
  const Comp = inline ? 'div' : 'p';
  return (
    <Comp className={`text-[11.5px] border rounded-v2-sm px-2.5 py-1.5 flex items-baseline gap-2 ${tone}`}>
      <FileText size={12} className="shrink-0" /> <span>{children}</span>
    </Comp>
  );
}

// ─── Bound runtime controls (POLICY_BINDING_RUNTIME_DESIGN Phase 2) ───
// Shows the policy controls actually bound to this skill at their PEPs, with
// per-row Remove. Framework-sourced bindings render read-only (a lock). The
// skill's policy set is locked in Act phase (Remove disabled). Re-binding a
// removed control happens in Policies (Bind stage) — this panel intentionally
// does not host a rule-picker; it surfaces + prunes what's already bound.
// Violation-action semantics (mirrors backend PolicyRule.Severity) — drives the
// per-control action badge so operators see the IMPLICATION of a violation at a glance.
const ACTION_META = {
  BLOCK: { label: 'BLOCK', cls: 'bg-v2-danger-soft text-v2-danger-strong', help: 'On violation: hard stop — the request is rejected immediately.' },
  WARN:  { label: 'WARN',  cls: 'bg-v2-warning-soft text-v2-warning-strong', help: 'On violation: flagged but allowed — surfaced in the response and the audit trail.' },
  AUDIT: { label: 'AUDIT', cls: 'bg-v2-surface-muted text-v2-ink-muted', help: 'On violation: logged only — no user-visible impact.' },
};

function BoundControlsSection({ skillId, skillPhase, reloadToken, onGoToBind }) {
  const [bindings, setBindings] = useState([]);
  const [state, setState] = useState('loading');
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [notice, setNotice] = useState(null);
  // Inline confirmation (NOT native window.confirm/prompt — those are silently
  // suppressed in the slideout context, which made Remove appear to do nothing).
  const [confirmId, setConfirmId] = useState(null);
  const [confirmText, setConfirmText] = useState('');

  const isActLocked = (skillPhase || '').toLowerCase() === 'act';

  const load = useCallback(async () => {
    setState('loading');
    setError(null);
    const r = await listSkillPolicyBindings(skillId);
    if (r?.ok === false) {
      setState('error');
      setError(r.error || r.message || 'binding lookup failed');
      return;
    }
    const list = Array.isArray(r?.data) ? r.data : Array.isArray(r) ? r : [];
    setBindings(list);
    setState('ready');
  }, [skillId]);

  useEffect(() => { load(); }, [load, reloadToken]);

  const startConfirm = (b) => {
    setNotice(null);
    setConfirmText('');
    setConfirmId(b.bindingId);
  };
  const cancelConfirm = () => { setConfirmId(null); setConfirmText(''); };

  // Performs the actual unbind once confirmed. No native dialogs.
  const doRemove = async (b) => {
    setNotice(null);
    setBusyId(b.bindingId);
    const r = await unbindSkillPolicyControl(b.bindingId);
    setBusyId(null);
    if (r?.ok === false) {
      // Surface the SERVER's precise 423 reason. A bare hardcoded "this skill is in Act"
      // is wrong for an AGENT-WIDE binding: the lock can fire because a DIFFERENT skill of
      // the agent is in Act (e.g. editing a Build skill but a sibling is still in Act). The
      // server message names the actual Act-phase skill and explains agent-wide scope.
      if (r.status === 423) {
        setNotice(r.message || r.error
          || 'This control is locked because a skill it gates is in Act phase. Regress that skill to Build/Decide to remove it.');
        return;
      }
      if (r.status === 403) { setNotice(r.message || 'Framework-applied control — detach the framework to remove it.'); return; }
      setNotice(r.error || r.message || 'Remove failed.');
      return;
    }
    cancelConfirm();
    load();
  };

  return (
    <div className="mt-3 border-t border-v2-border-subtle pt-2.5">
      <div className="flex items-center gap-2 mb-1.5">
        <ShieldCheck size={13} className="text-v2-trust-strong" />
        <span className="text-[11.5px] font-semibold text-v2-ink">
          Bound runtime controls{state === 'ready' ? ` · ${bindings.length}` : ''}
        </span>
      </div>

      {state === 'loading' && (
        <div className="text-[11.5px] text-v2-ink-muted flex items-center gap-2">
          <Loader2 size={12} className="animate-spin" /> Loading bindings…
        </div>
      )}
      {state === 'error' && <ErrorRow text={error} />}

      {state === 'ready' && bindings.length === 0 && (
        <div className="text-[11px] text-v2-ink-muted">
          No deterministic controls bound yet — this skill enforces via advisory RAG only.
          {typeof onGoToBind === 'function' && (
            <button
              type="button"
              className="ml-1 underline text-v2-trust-strong"
              onClick={onGoToBind}
            >Bind controls in Policies →</button>
          )}
        </div>
      )}

      {state === 'ready' && bindings.length > 0 && (
        <ul className="space-y-1.5">
          {bindings.map((b) => {
            const blocking = (b.enforcementLevel || '').toUpperCase() === 'BLOCK';
            const confirming = confirmId === b.bindingId;
            return (
            <li key={b.bindingId} className="text-[12px] text-v2-ink">
              <div className="flex items-center gap-2">
                <span className="font-semibold truncate max-w-[40%]">{b.ruleName || b.policyRuleId}</span>
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-v2-action-soft text-v2-action-strong">
                  {b.enforcementPoint}
                </span>
                {b.enforcementLevel && (() => {
                  const m = ACTION_META[(b.enforcementLevel || '').toUpperCase()]
                    || { label: b.enforcementLevel, cls: 'bg-v2-surface-muted text-v2-ink-muted', help: '' };
                  return (
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${m.cls}`} title={m.help}>
                      {m.label}
                    </span>
                  );
                })()}
                <span
                  className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${b.appliesTo === 'agent' ? 'bg-v2-trust-soft text-v2-trust-strong' : 'bg-v2-surface-muted text-v2-ink-muted'}`}
                  title={b.appliesTo === 'agent'
                    ? 'Agent-wide — gates every skill of this agent via the runtime PEP'
                    : 'Scoped to this skill only'}>
                  {b.appliesTo === 'agent' ? 'whole agent' : 'this skill'}
                </span>
                {b.sourceTag && (
                  <span className="text-[10px] text-v2-ink-muted">{b.sourceTag}</span>
                )}
                <span className="ml-auto">
                  {b.removable === false ? (
                    <span className="text-[10.5px] text-v2-ink-muted flex items-center gap-1" title="Applied by a compliance framework — read-only here">
                      <Lock size={11} /> framework
                    </span>
                  ) : isActLocked ? (
                    <span className="text-[10.5px] text-v2-ink-muted flex items-center gap-1" title="Skill is in Act phase — bindings locked">
                      <Lock size={11} /> locked
                    </span>
                  ) : !confirming ? (
                    <button
                      type="button"
                      disabled={busyId === b.bindingId}
                      onClick={() => startConfirm(b)}
                      className="text-[10.5px] text-v2-warning-strong flex items-center gap-1 disabled:opacity-50"
                    >
                      <Trash2 size={11} /> Remove
                    </button>
                  ) : null}
                </span>
              </div>

              {(b.description || b.targetField || b.ruleType) && (
                <div className="mt-0.5 ml-0.5 text-[11px] text-v2-ink-muted leading-snug">
                  {b.description
                    || `${String(b.ruleType || 'control').replace(/_/g, ' ')}${b.targetField ? ` · target: ${b.targetField}` : ''}`}
                </div>
              )}

              {/* Inline confirm — replaces native dialogs. Blocking controls
                * require type-to-confirm (design §2.4); others confirm directly. */}
              {confirming && (
                <div className="mt-1.5 ml-1 flex flex-wrap items-center gap-2 rounded-v2-sm border border-v2-warning/30 bg-v2-warning-soft px-2 py-1.5">
                  <span className="text-[10.5px] text-v2-warning-strong">
                    {blocking
                      ? <>Removing a <strong>BLOCKING</strong> control stops enforcement at {b.enforcementPoint}. Type <strong>REMOVE</strong> to confirm.</>
                      : <>Remove this binding? The rule itself is kept.</>}
                  </span>
                  {blocking && (
                    <input
                      autoFocus
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      placeholder="REMOVE"
                      className="text-[11px] px-1.5 py-0.5 rounded border border-v2-border-subtle bg-v2-surface w-24"
                    />
                  )}
                  <button
                    type="button"
                    disabled={busyId === b.bindingId || (blocking && confirmText.trim().toUpperCase() !== 'REMOVE')}
                    onClick={() => doRemove(b)}
                    className="text-[10.5px] font-semibold text-white bg-v2-danger rounded px-2 py-0.5 flex items-center gap-1 disabled:opacity-40"
                  >
                    {busyId === b.bindingId
                      ? <Loader2 size={11} className="animate-spin" />
                      : <Trash2 size={11} />} Confirm remove
                  </button>
                  <button
                    type="button"
                    onClick={cancelConfirm}
                    className="text-[10.5px] text-v2-ink-muted hover:text-v2-ink"
                  >Cancel</button>
                </div>
              )}
            </li>
          );})}
        </ul>
      )}

      {notice && (
        <div className="mt-1.5 text-[10.5px] text-v2-warning-strong">{notice}</div>
      )}
    </div>
  );
}

export default SkillLinkedResourcePanel;
